<script lang="ts">
	import type { CrudStore } from '@isoftdata/svelte-store-crud'
	import type { States, Vendor, PurchaseAgents } from './vendor'
	import type { i18n } from 'types/common'

	import BillingAndAlternateAddress, {
		type BillingAddress,
		type AlternateAddress,
	} from 'components/BillingAndAlternateAddress.svelte'
	import { v4 as uuid } from '@lukeed/uuid'
	import { getContext } from 'svelte'
	import Input from '@isoftdata/svelte-input'
	import Select from '@isoftdata/svelte-select'
	import { getEventValue } from '@isoftdata/browser-event'

	interface Props {
		vendor: Vendor
		states?: States
		purchaseAgents?: PurchaseAgents
		vendorChanged: boolean
		viewOnlyMode?: boolean
		enableAddressValidation?: boolean
	}

	let {
		vendor = $bindable(),
		states = [],
		purchaseAgents = [],
		vendorChanged = $bindable(),
		viewOnlyMode = false,
		enableAddressValidation = false,
	}: Props = $props()

	const alternateAddressCrudStore = getContext<CrudStore<AlternateAddress, 'uuid'>>('alternateAddressCrudStore')

	let billingAddress: BillingAddress = $state(formatVendorBillingAddress(vendor))
	let alternateAddresses: Array<AlternateAddress> = $state(formatVendorAlternateAddresses(vendor.addresses))

	const { t: translate } = getContext<i18n>('i18next')

	function formatVendorBillingAddress(vendor: Vendor): BillingAddress {
		return {
			companyName: vendor.companyName,
			contactName: vendor.contactName,
			street: vendor.billingAddress.address1,
			city: vendor.billingAddress.city,
			state: vendor.billingAddress.state,
			zip: vendor.billingAddress.zip,
			country: vendor.billingAddress.country,
			email: vendor.email,
			faxNumber: vendor.faxNumber,
			phoneNumber: vendor.phoneNumber,
			mobilePhoneNumber: vendor.mobilePhoneNumber,
			validated: vendor.billingAddressValidated,
		}
	}

	function formatVendorAlternateAddresses(addresses: Vendor['addresses']): Array<AlternateAddress> {
		let formatedAlternateAddresses: Array<AlternateAddress> = addresses.map(address => {
			return {
				id: address.vendorAddressId,
				uuid: uuid(),
				label: address.title,
				primary: address.primary,
				addressData: {
					companyName: address.company,
					contactName: address.contact,
					street: address.address1,
					city: address.city,
					state: address.state,
					zip: address.zip,
					country: address.country,
					email: address.email ?? '',
					faxNumber: address.faxNumber,
					phoneNumber: address.phone,
					mobilePhoneNumber: address.mobilePhoneNumber,
					department: address.department,
					notes: address.notes ?? '',
					purchaseAgent: null,
					purchaseRegion: null,
					validated: !!address.lastValidated,
				},
			}
		})

		// TODO: maybe we need to optimize this logic later
		// The issue here with this fix is that, the vendor.addresses object does not have all the changes
		// Due to that, if the user leave the page without saving, then come back to the vendor address page,
		// the changes made to the alternate address will be lost.
		if (alternateAddressCrudStore.createdValues.length) {
			alternateAddressCrudStore.createdValues.forEach(newAddress => {
				formatedAlternateAddresses.push({
					id: newAddress.id,
					uuid: newAddress.uuid,
					label: newAddress.label,
					primary: newAddress.primary,
					addressData: newAddress.addressData,
				})
			})
		}

		if (alternateAddressCrudStore.updatedValues.length) {
			alternateAddressCrudStore.updatedValues.forEach(updatedAddress => {
				const index = formatedAlternateAddresses.findIndex(addr => addr.id === updatedAddress.id)
				if (index !== -1) {
					formatedAlternateAddresses[index] = {
						id: updatedAddress.id,
						uuid: updatedAddress.uuid,
						label: updatedAddress.label,
						primary: updatedAddress.primary,
						addressData: updatedAddress.addressData,
					}
				}
			})
		}

		return formatedAlternateAddresses
	}

	function updateVendorBillingAddress(updatedBillingAddress: BillingAddress) {
		vendor.companyName = updatedBillingAddress.companyName
		vendor.contactName = updatedBillingAddress.contactName
		vendor.billingAddress = {
			...vendor.billingAddress,
			address1: updatedBillingAddress.street,
			city: updatedBillingAddress.city,
			state: updatedBillingAddress.state,
			zip: updatedBillingAddress.zip,
			country: updatedBillingAddress.country,
		}
		vendor.faxNumber = updatedBillingAddress.faxNumber
		vendor.mobilePhoneNumber = updatedBillingAddress.mobilePhoneNumber
		vendor.billingAddressValidated = updatedBillingAddress.validated
	}
</script>

<BillingAndAlternateAddress
	{states}
	{viewOnlyMode}
	{enableAddressValidation}
	{alternateAddressCrudStore}
	bind:billingAddress
	bind:alternateAddresses
	bind:hasChanges={vendorChanged}
	billingAddressChange={billingAddress => updateVendorBillingAddress(billingAddress)}
>
	{#snippet billingTopFields()}
		<div class="col-12 col-md-6">
			<Input
				label={translate('vendor.vendorCode', 'Vendor Code')}
				value={vendor.code ?? ''}
				disabled={viewOnlyMode}
				onchange={event => {
					vendor.code = getEventValue(event)
					vendorChanged = true
				}}
			/>
		</div>
		<div class="col-12 col-md-6">
			<Input
				label={translate('vendor.accountNumber', 'Account Number')}
				value={vendor.accountNumber || ''}
				disabled={viewOnlyMode}
				onchange={event => {
					vendor.accountNumber = getEventValue(event)
					vendorChanged = true
				}}
			/>
		</div>
	{/snippet}
	{#snippet billingAdditionalFields()}
		<div class="col-12 col-md-6">
			<Input
				readonly
				label={translate('vendor.purchaseRegion', 'Purchase Region')}
				value={vendor.purchaseRegion?.name ?? ''}
			/>
		</div>
		<div class="col-12 col-md-6">
			<Select
				label={translate('vendor.purchaseAgent', 'Purchase Agent')}
				emptyText={translate('vendor.noPurchaseAgent', 'No Purchase Agent')}
				options={purchaseAgents.map(agent => agent.name)}
				disabled={viewOnlyMode}
				value={vendor.purchaseAgent?.name ?? null}
				onchange={event => {
					const value = getEventValue(event)
					const selectedPurchaseAgent = purchaseAgents.find(agent => agent.name === value)
					if (selectedPurchaseAgent) {
						vendor.purchaseAgent = selectedPurchaseAgent
					}
					vendorChanged = true
				}}
			/>
		</div>
	{/snippet}
</BillingAndAlternateAddress>
